import { IconCheck, IconX } from "@tabler/icons-react";

import { v4 } from "uuid";

import {
  Button,
  Divider,
  Flex,
  Group,
  Modal,
  Stack,
  Text,
  TextInput,
  ThemeIcon,
} from "@mantine/core";
import { useDisclosure, useMediaQuery } from "@mantine/hooks";
import { useEffect, useState } from "react";
import { ISynthesis } from "../../../types/ISynthesis";
import MarkAPI from "../../../api/mark.api";
import PDFViewer from "../../Book/Viewer";

interface ISynthesisProps {
  label: string;
  data: ISynthesis;
  idVideo: string;
}

interface ISynthesisWordAnswer {
  word: string;
  page: number;
  studentAnswer: string;
  id: string;
}

const Synthesis: React.FC<ISynthesisProps> = ({ label, data, idVideo }) => {
  const [displayCurrentPage, setCurrentPage] = useState(0);
  const [pageNumber, setPageNumber] = useState(0);
  const [isBookLoading, setBookLoader] = useState(false);
  const [opened, { open, close }] = useDisclosure(false);
  const [wordsData, setWordsData] = useState<ISynthesisWordAnswer[]>([]);
  const [showCorrection, setShowCorrection] = useState(false);
  const [showScore, setShowScore] = useState(false);
  const isMobile = useMediaQuery("(max-width: 75em)");
  const [score, setScore] = useState(0);

  const markSynthesis = (mark: number) => {
    MarkAPI.setMark(idVideo, mark, "video");
  };

  const handleChangeStudentAnswer = (id: string, value: string) => {
    const newWords = [...wordsData];
    const indexWord = newWords.findIndex((w) => w.id === id);
    let word;

    if (indexWord === -1) return;
    word = newWords[indexWord];
    newWords.splice(indexWord, 1, { ...word, studentAnswer: value });

    setShowCorrection(false);
    setShowScore(false);
    setWordsData(newWords);
  };

  const handleShowCorrection = (word: ISynthesisWordAnswer) => {
    if (!showCorrection) return;

    if (word.studentAnswer !== word.word) {
      return (
        <Group
          style={{
            gap: 5,
          }}
        >
          Réponse attendue :
          <Text size={30} color="green">
            {word.word}
          </Text>
        </Group>
      );
    }
    return;
  };

  const handleCalculateScore = () => {
    const wordsCount = wordsData.length;

    const score = wordsData.reduce((acc, word) => {
      if (word.studentAnswer === word.word) {
        return acc + 1;
      }
      return acc;
    }, 0);

    setScore(Math.ceil((score * 20) / wordsCount));
    setShowScore(true);
    markSynthesis(score);
  };

  const nextPage = () => {
    setShowCorrection(false);
    setShowScore(false);
    setCurrentPage(displayCurrentPage + 1);
  };

  const previousPage = () => {
    setShowCorrection(false);
    setShowScore(false);
    setCurrentPage(displayCurrentPage - 1);
  };

  useEffect(() => {
    setWordsData(
      data.words.map((word) => ({
        word: word.word,
        page: word.page,
        studentAnswer: "",
        id: v4(),
      }))
    );
  }, [data]);

  return (
    <>
      <Modal
        opened={opened}
        onClose={close}
        title={`${label} : ${data.title}`}
        size={isMobile ? undefined : "70%"}
        fullScreen={isMobile ?? false}
      >
        <div style={{ overflow: "scroll", maxHeight: "40svh" }}>
          <PDFViewer
            endPage={displayCurrentPage + 1}
            initialPage={displayCurrentPage}
            fileURL={data.link}
            setBookLoader={setBookLoader}
            getNbPage={(pageMax) => {
              setPageNumber(pageMax);
            }}
            goToTop={false}
          />
        </div>
        <Divider my={30} />
        <Stack style={{ overflow: "scroll", maxHeight: "20svh" }}>
          {wordsData.filter((w) => w.page === displayCurrentPage + 1).length ===
            0 && (
            <Button disabled my={20}>
              Aucun mot sur cette page
            </Button>
          )}
          {wordsData
            .filter((w) => w.page === displayCurrentPage + 1)
            .map((word, index) => (
              <TextInput
                key={index}
                label={`Mot n°${index + 1}`}
                value={word.studentAnswer}
                onChange={(e) =>
                  handleChangeStudentAnswer(word.id, e.target.value)
                }
                error={handleShowCorrection(word)}
                rightSection={
                  <>
                    {showCorrection &&
                      (word.studentAnswer === word.word ? (
                        <ThemeIcon color="green">
                          <IconCheck />
                        </ThemeIcon>
                      ) : (
                        <ThemeIcon color="red">
                          <IconX />
                        </ThemeIcon>
                      ))}
                  </>
                }
              />
            ))}
        </Stack>

        <Flex gap={10} align={"center"} py={20} direction={"column"}>
          <Button.Group>
            <Button color="secondary.4" onClick={() => setShowCorrection(true)}>
              Corriger
            </Button>
            {displayCurrentPage === pageNumber - 1 && (
              <Button
                color="secondary.4"
                onClick={() => handleCalculateScore()}
              >
                Calculer ma note
              </Button>
            )}
          </Button.Group>
          {showScore && <div>Ma note est {score}/20</div>}
          <div>
            <Button.Group>
              <Button disabled={displayCurrentPage <= 0} onClick={previousPage}>
                Précédant
              </Button>
              <Button>
                {displayCurrentPage + 1}/{pageNumber}
              </Button>
              <Button
                disabled={displayCurrentPage >= pageNumber - 1}
                onClick={nextPage}
              >
                Suivant
              </Button>
            </Button.Group>
          </div>
        </Flex>
      </Modal>

      <Button onClick={open} color="primary.5" radius={"xl"}>
        {label}
      </Button>
    </>
  );
};

export default Synthesis;